/**
 * DispatchConsole - 调度控制台主页面组件
 * 
 * 这是一个企业级的容器调度管理系统的主要页面组件。
 * 采用模块化架构，将复杂的功能拆分为多个可重用的子组件。
 * 
 * 主要功能：
 * - 容器任务管理和调度
 * - 高级搜索和过滤
 * - 数据表格展示和操作
 * - 实时统计和状态监控
 * - 司机分配和任务跟踪
 * 
 * 架构特点：
 * - 单一职责原则：主组件只负责状态管理和组件组合
 * - 模块化设计：功能拆分为独立的子组件
 * - 可维护性：清晰的代码结构和注释
 * 
 * @author Claude Code Assistant
 * @version 2.0 - 重构版本，从504行优化到246行
 */

import { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { TooltipProvider } from "../components/ui/tooltip";
import { handleTokenFromUrl } from "../utils/auth";

// 导入模块化组件
import { DispatchSidebar } from "../components/dispatch/DispatchSidebar";
import { DispatchHeader } from "../components/dispatch/DispatchHeader";
import { SearchFilters } from "../components/dispatch/SearchFilters";
import { DispatchTable } from "../components/dispatch/DispatchTable";
import { StatisticsCards } from "../components/dispatch/StatisticsCards";

// Phase 3: 错误边界和加载骨架屏
import { ErrorBoundary } from "../components/common/ErrorBoundary";
import { TableSkeleton, StatisticsSkeleton, FiltersSkeleton } from "../components/common/TableSkeleton";

// Container Monitor Modal
import { ContainerMonitorModal } from "../components/dispatch/modals/ContainerMonitorModal";
import { OperationFailuresModal } from "../components/dispatch/modals/OperationFailuresModal";
import { useOperationFailures } from "../hooks/useOperationFailures";
// 批量预约：弹窗挂在页面这一层，不进表格行 —— 每行各渲染一个 Modal 会让长列表
// 卡到没法用（见 feedback_modal_performance）。
import { BatchBookingModal } from "../components/dispatch/modals/BatchBookingModal";
// 批量付闸口费 —— 同样挂在页面层，理由同上（每行一个 Modal 会拖垮长列表）。
import { GateFeeBatchModal } from "../components/dispatch/modals/GateFeeBatchModal";
import { useBookingFlow } from "../hooks/useBookingFlow";

// 导入自定义Hooks和数据源
import { useTaskData } from "../hooks/useTaskData";
import { usePagination } from "../hooks/usePagination";
import { useDispatchTasks } from "../hooks/useDispatchTasks";
import { statusConfig, taskTypeConfig, importExportOptions } from "../data/dispatchData";
import { EntityLinkProvider } from "../contexts/EntityLinkContext";
import { SortField, ContainerTask } from "../types/container";
// checkSidebarNavigationState moved into DispatchSidebar
import { exportToCSV, exportToExcel } from "../utils/exportUtils";
import { preloadDropdownData } from "../components/dispatch/utils/taskConstants";
import { socketService } from "../services/socket";
import { notification } from "antd";

/**
 * DispatchConsole 主组件
 * 负责整个调度控制台的状态管理和组件协调
 */
export default function DispatchConsole() {
  // ==================== Hooks ====================
  const navigate = useNavigate()
  const location = useLocation()
  // Determine route type early so hooks can skip work on nested routes
  const isMainPage = location.pathname === '/dispatch-console' || location.pathname === '/dispatch-console/tasks'
  const isNestedRoute = !isMainPage && location.pathname.startsWith('/dispatch-console/')
  // Check synchronously if OAuth code is in URL — block rendering until exchange completes
  const [oauthReady, setOauthReady] = useState(
    () => !new URLSearchParams(window.location.search).has('code')
  )

  // Handle Google OAuth token from URL (async: also fetches user info).
  // After exchange succeeds, do a full reload so PermissionContext and the
  // sidebar's user info pick up the freshly-stored token/user from localStorage
  // (they read it on mount, which happened before the exchange completed).
  useEffect(() => {
    if (!oauthReady) {
      handleTokenFromUrl()
        .then((accessToken) => {
          if (accessToken) {
            window.location.replace(window.location.pathname)
          } else {
            setOauthReady(true)
          }
        })
        .catch(console.error)
    }
  }, [oauthReady])

  // Pre-load dropdown data for task detail modal to prevent loading flicker
  useEffect(() => {
    if (oauthReady) preloadDropdownData()
  }, [oauthReady])

  // Ensure socket is connected for global events (driver_left_company, etc.)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !socketService.isSocketConnected()) {
      socketService.connect(token).catch(console.error);
    }

    const handleDriverJoined = (data: unknown) => {
      const { driver_name, driver_id } = data as { driver_name: string; driver_id: number };
      notification.success({
        message: 'New Driver Joined',
        description: `${driver_name || `Driver #${driver_id}`} has joined the company.`,
        duration: 6,
      });
    };
    const handleDriverLeft = (data: unknown) => {
      const { driver_name, driver_id } = data as { driver_name: string; driver_id: number };
      notification.warning({
        message: 'Driver Left Company',
        description: `${driver_name || `Driver #${driver_id}`} has left the company.`,
        duration: 6,
      });
    };
    socketService.on('driver_joined_company', handleDriverJoined);
    socketService.on('driver_left_company', handleDriverLeft);
    return () => {
      socketService.off('driver_joined_company', handleDriverJoined);
      socketService.off('driver_left_company', handleDriverLeft);
    };
  }, [])
  
  // ==================== 状态管理 ====================
  
  // 搜索和过滤相关状态
  const [searchTerm, setSearchTerm] = useState("")                    // 搜索关键词
  const [selectedDriver, setSelectedDriver] = useState("all")         // 选中的司机
  const [selectedStatus, setSelectedStatus] = useState("all")        // 选中的状态（默认 All Tasks）
  const [selectedShipper, setSelectedShipper] = useState("all")       // 选中的货主
  const [selectedOrderingParty, setSelectedOrderingParty] = useState("all") // 选中的下单方 (Ord. Party 列点击筛选)
  const [selectedTasks, setSelectedTasks] = useState<string[]>([])    // 选中的任务ID列表
  const [importExportFilter, setImportExportFilter] = useState("all") // 进出口过滤器
  const [destinationFilter, setDestinationFilter] = useState("all")   // 目的地过滤器
  const [portFilter, setPortFilter] = useState("all")                 // 码头过滤器
  const [lfdFilter, setLfdFilter] = useState("all")                   // LFD时间过滤器
  const [customLfdDate, setCustomLfdDate] = useState("")              // 自定义LFD日期
  const [completedTimeFilter, setCompletedTimeFilter] = useState("7days")  // 已完成任务时间过滤器
  const [customCompletedDate, setCustomCompletedDate] = useState("")       // 自定义完成日期

  // UI交互状态
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set()) // 展开的表格行
  const [expandedMenus, setExpandedMenus] = useState({                     // 侧边栏菜单展开状态
    container: true,
    driver: false,
    equipment: false,
    client: false,
    billPayment: false,
    dashboard: false,
    counting: false,
  })
  // Sidebar collapsed state moved into DispatchSidebar to avoid parent re-renders

  // 排序状态
  const [sortField, setSortField] = useState<SortField | null>(null)     // 排序字段
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null) // 排序方向
  
  // 收藏和标记状态
  const [markedTaskIds, setMarkedTaskIds] = useState<Set<string>>(new Set()) // 标记的任务ID集合
  const [showMarkedOnly, setShowMarkedOnly] = useState(false)                // 是否只显示标记的任务

  // Container Monitor 弹窗
  const [showMonitorModal, setShowMonitorModal] = useState(false)
  const [showFailuresModal, setShowFailuresModal] = useState(false)
  // Badge only — the list itself loads when the modal opens.
  const { counts: failureCounts } = useOperationFailures()

  // ==================== API 数据获取 ====================
  // 使用 useDispatchTasks Hook 从 API 获取真实数据
  // Phase 5: 启用实时更新功能
  const {
    tasks: containerData,
    drivers,
    shippers,
    destinations,
    ports,
    stats: apiStats,
    loading: tasksLoading,
    error: tasksError,
    updateTask,
    // B3 Fix Plate — handed down to the task rows so the red pulsing button
    // opens eModal directly instead of only pointing at the detail modal.
    startFixPlate,
    // Gate Fee — same idea, one click from the row to eModal's fee payment page.
    startGateFee,
    startGateFeeBatch,
    refetch: refetchTasks,
    // 后端说「一共有这么多条」。和真正发回来的条数一比，就知道这一屏是不是被
    // 截断了 —— 见下面 isTruncated。
    total: totalTasks,
    // Phase 5: 实时连接状态
    isRealtimeConnected,
    lastRealtimeUpdate,
    lastRefreshTime
  } = useDispatchTasks({ autoFetch: isMainPage && oauthReady, enableRealtime: isMainPage && oauthReady })

  /**
   * 这一屏是不是只有一部分任务。
   *
   * 整个板子是「一次把任务全拉回来、在浏览器里筛和排」，而后端一次最多给 2000 条
   * （防资源耗尽的闸）。超过之后多出来的那些**静默消失**：筛选、统计、排序全都
   * 只对拉回来的那一批生效，而页面照常显示，看上去和数据齐全时一模一样。
   *
   * 排序尤其会骗人 —— On Vessel 默认「最老的单在最前」，可被截掉的恰恰是最老的
   * 那一批（后端默认按任务创建时间倒序发），于是列表第一行会理直气壮地显示一个
   * 并不是最老的单。
   *
   * 所以这条必须出声。按 [[feedback_ui_message_only_report_failures]]：办成了不
   * 吭声，这里是真没办成（给的不是全部），不说才是问题。
   */
  const isTruncated = totalTasks > 0 && containerData.length > 0 && totalTasks > containerData.length

  // ==================== 数据处理 ====================
  
  // 使用自定义Hook处理数据过滤、排序和格式化
  const {
    sortedData,
    formatDateTime,
    getLFDUrgency,
    getLFDStyles,
  } = useTaskData({
    data: containerData,
    searchTerm,
    selectedDriver,
    selectedShipper,
    selectedOrderingParty,
    selectedStatus,
    lfdFilter,
    customLfdDate,
    importExportFilter,
    destinationFilter,
    portFilter,
    showMarkedOnly,
    markedTaskIds,
    sortField,
    sortDirection,
    completedTimeFilter,
    customCompletedDate,
  })

  // 使用分页Hook处理数据分页显示
  const paginationResult = usePagination(sortedData, 10)

  // 下单方下拉选项：直接从已加载的任务里取，保证选项与列表里能筛出来的值一一对应
  const orderingParties = useMemo(
    () =>
      Array.from(
        new Set(containerData.map((t) => t.orderingPartyName).filter((n): n is string => !!n))
      ).sort((a, b) => a.localeCompare(b)),
    [containerData]
  )

  // 码头下拉选项：Port 主数据（本租户独占）。按 id 筛选，显示短名
  const portOptions = useMemo(
    () =>
      ports
        .map((p) => ({ id: p.id, name: p.name, shortName: p.shortName || p.code }))
        .sort((a, b) => (a.shortName || a.name).localeCompare(b.shortName || b.name)),
    [ports]
  )

  // 仓库下拉选项：Warehouse 主数据（本租户独占）。按 id 筛选，显示短名
  const warehouseOptions = useMemo(
    () =>
      [...destinations].sort((a, b) =>
        (a.shortName || a.name).localeCompare(b.shortName || b.name)
      ),
    [destinations]
  )

  /**
   * Ord. Party 列点击筛选：点当前已筛选的值则取消筛选
   */
  const handleOrderingPartyClick = (name: string) =>
    setSelectedOrderingParty((prev) => (prev === name ? "all" : name))

  // Handle taskId from URL query params (e.g., from Messages task reference click, or Driver Payment "View Task" button)
  // — stored in state so DispatchTable can pick it up and open its detail modal
  const [autoOpenTaskId, setAutoOpenTaskId] = useState<string | null>(null);
  useEffect(() => {
    if (!isMainPage || containerData.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const taskIdParam = params.get('taskId');
    // `container` is the Failed Operations centre's entry point: some failures
    // (a status check that never resolved to a task) only ever knew the
    // container number, so taskId alone would leave those rows unclickable.
    const containerParam = params.get('container');
    if (!taskIdParam && !containerParam) return;

    const container = taskIdParam
      ? containerData.find(c => c.id === taskIdParam || c.taskId === taskIdParam)
      : containerData.find(c => c.containerNumber === containerParam);

    // Container known but no matching task in the current data set (stopped
    // monitor, task deleted, different tenant). Still filter down to it —
    // an empty filtered list tells the dispatcher "not here", which beats
    // dumping them on an unfiltered board with no explanation.
    if (!container && containerParam) {
      setSearchTerm(containerParam);
    }

    if (container) {
      // The default "all" status filter hides completed/cancelled/uncompleted tasks
      // (and a completed-time window further limits them). When deep-linking straight
      // to such a task (e.g. Driver Payment "View" on a completed yard leg), switch the
      // filter to its status and drop the time window so the row renders and its detail
      // can auto-open instead of landing on an empty/filtered list.
      const terminalStatuses = ['completed', 'cancelled', 'uncompleted'];
      if (terminalStatuses.includes(container.status)) {
        setSelectedStatus(container.status);
        setCompletedTimeFilter('all');
      }
      setExpandedRows(new Set([container.id]));
      setSearchTerm(container.containerNumber || taskIdParam || containerParam || '');
      setAutoOpenTaskId(container.id);
      setTimeout(() => {
        const el = document.getElementById(`container-row-${container.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
    // Clean up URL
    params.delete('taskId');
    params.delete('container');
    const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [isMainPage, containerData]);

  // 处理从driver-task页面传递过来的container选择
  useEffect(() => {
    const selectedContainerData = sessionStorage.getItem('selectedContainerForView')
    if (selectedContainerData) {
      try {
        const { taskId, containerNumber } = JSON.parse(selectedContainerData)
        
        // 通过task id找到对应的container
        const container = containerData.find(c => c.taskId === taskId || c.id === taskId)
        
        if (container) {
          // 设置选中的container
          setSelectedTasks([container.id])
          // 展开该container的详情行
          setExpandedRows(new Set([container.id]))
          // 可以添加搜索以快速定位到该container
          setSearchTerm(containerNumber)
          
          // 延迟一下确保页面渲染完成后滚动到该container
          setTimeout(() => {
            const element = document.getElementById(`container-row-${container.id}`)
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }, 500)
        }
        
        // 清除sessionStorage中的数据
        sessionStorage.removeItem('selectedContainerForView')
      } catch (error) {
        console.error('Error parsing selected container data:', error)
      }
    }
  }, [containerData])

  // ==================== 事件处理函数 ====================
  
  /**
   * 切换侧边栏菜单的展开/折叠状态
   * @param menu - 菜单项名称 (container | equipment | client | counting)
   */
  const toggleMenu = (menu: keyof typeof expandedMenus) =>
    setExpandedMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }))

  // Sidebar mouse handlers moved into DispatchSidebar

  /**
   * 切换表格行的展开/折叠状态
   * @param taskId - 任务ID
   */
  const toggleRowExpansion = (taskId: string) =>
    setExpandedRows((prev) => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(taskId)) newExpanded.delete(taskId)
      else newExpanded.add(taskId)
      return newExpanded
    })

  /**
   * 切换任务的标记状态（收藏/取消收藏）
   * @param taskId - 任务ID
   */
  const toggleMarked = (taskId: string) =>
    setMarkedTaskIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(taskId)) {
        newSet.delete(taskId)
      } else {
        newSet.add(taskId)
      }
      return newSet
    })

  /**
   * 处理表格排序
   * 点击同一字段：升序 -> 降序 -> 无排序
   * @param field - 排序字段
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc")
      } else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  /**
   * 处理任务调度操作
   * 导航到调度任务页面
   */
  const handleDispatchTask = () => {
    navigate('/dispatch-console/dispatch-tasks')
  }

  /**
   * 处理添加容器操作
  /**
   * 导出 CSV 文件
   */
  const handleExportCSV = () => {
    exportToCSV(sortedData, 'dispatch_tasks')
  }

  /**
   * 导出 Excel 文件
   */
  const handleExportExcel = () => {
    exportToExcel(sortedData, 'dispatch_tasks')
  }

  /**
   * 刷新数据
   */
  const handleResetData = () => {
    if (confirm('Refresh data?')) {
      refetchTasks()
      console.log('Data refreshed from API')
    }
  }

  /**
   * 处理编辑容器详情操作 - 现在使用内嵌编辑
   * @param container - 要编辑的容器任务
   */
  // B3 Fix Plate — stable reference so the table's rows don't get a new prop
  // identity on every console re-render.
  const handleFixPlate = useCallback((appointmentId: number) => {
    void startFixPlate(appointmentId)
  }, [startFixPlate])

  // 列表里的收据图标改成开「批量付闸口费」弹窗 —— 欠费的柜往往不止一只，而
  // eModal 一次查费能装 50 只，一只一只点进去付是白费的。任务详情里那个入口
  // 仍然是单柜直付。
  const [gateFeeAnchorId, setGateFeeAnchorId] = useState<number | null>(null)

  const handleGateFee = useCallback((taskId: number | string) => {
    const numericId = typeof taskId === 'number' ? taskId : parseInt(taskId, 10)
    if (!Number.isFinite(numericId)) return
    setGateFeeAnchorId(numericId)
  }, [])

  const handleGateFeeConfirm = useCallback(async (taskIds: number[]) => {
    const result = await startGateFeeBatch(taskIds)
    if (!result.ok && result.error) {
      // 后端会明确说不支持的原因（进出口混选、都没柜号），原样告诉调度。
      notification.warning({
        message: 'Cannot start this gate fee payment',
        description: result.error,
        duration: 8,
      })
    }
    return result.ok
  }, [startGateFeeBatch])

  // ==================== 批量预约 ====================
  //
  // 一次 eModal 向导里约多只柜。行上的日历图标只负责说"从这只柜开始"，
  // 候选列表、勾选和交给插件都在弹窗里。
  //
  // 预约落地后的刷新走 onCompleted → refetchTasks；漏单的提醒由
  // useBookingFlow 自己弹（一条不会自动消失的红条），这里不重复。
  const [batchBookAnchorId, setBatchBookAnchorId] = useState<number | null>(null)

  const { startBatchBooking } = useBookingFlow({
    onCompleted: () => { void refetchTasks() },
    onBatchSummary: () => { void refetchTasks() },
  })

  const handleBatchBook = useCallback((taskId: number | string) => {
    const numericId = typeof taskId === 'number' ? taskId : parseInt(taskId, 10)
    if (!Number.isFinite(numericId)) return
    setBatchBookAnchorId(numericId)
  }, [])

  const handleBatchBookConfirm = useCallback(async (taskIds: number[]) => {
    const result = await startBatchBooking(taskIds)
    if (!result.ok && result.error) {
      // 不是"点了没反应"的那种失败 —— 后端会明确说不支持的原因（混码头、
      // 超过 10 只、某只柜缺提单号），这些都要原样告诉调度。
      notification.warning({
        message: 'Cannot start this booking',
        description: result.error,
        duration: 8,
      })
      return false
    }
    return result.ok
  }, [startBatchBooking])

  const handleEditContainer = (container: ContainerTask) => {
    console.log("编辑容器（内嵌模式）:", container.containerNumber)
    // 内嵌编辑由 TaskTableRow 直接处理，这里不需要额外操作
  }

  /**
   * 更新容器数据 - 用于内嵌编辑的数据同步
   * @param updatedContainer - 更新后的容器数据
   * @throws Error 如果保存失败
   */
  const handleUpdateContainer = async (updatedContainer: ContainerTask): Promise<void> => {
    console.log("DispatchConsole - 更新容器数据:", updatedContainer.containerNumber, updatedContainer)

    // 调用 API 更新任务
    const taskId = parseInt(updatedContainer.id)
    if (isNaN(taskId)) {
      console.warn('Invalid task ID, cannot update via API')
      throw new Error('Invalid task ID')
    }

    // updateTask internally detects status changes and routes them through
    // the dedicated status API (TaskStateMachine) before saving other fields.
    await updateTask(taskId, updatedContainer)
    console.log('Task updated via API')
  }

  /**
   * 处理全选/取消全选操作
   * @param checked - 是否选中
   */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTasks(sortedData.map((item) => item.id))
    } else {
      setSelectedTasks([])
    }
  }

  /**
   * 处理单个任务的选择状态
   * @param taskId - 任务ID
   * @param checked - 是否选中
   */
  const handleSelectTask = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTasks((prev) => [...prev, taskId])
    } else {
      setSelectedTasks((prev) => prev.filter((id) => id !== taskId))
    }
  }

  // ==================== 统计数据计算 ====================
  // 使用从 API Hook 获取的统计数据，并添加 marked 数量
  const stats = {
    ...apiStats,
    marked: markedTaskIds.size,
  }

  // ==================== 组件渲染 ====================
  
  // isMainPage / isNestedRoute computed above (near hooks)
  
  // Show loading while OAuth code exchange is in progress
  if (!oauthReady) {
    return <div className="h-screen flex items-center justify-center text-slate-500">Signing in…</div>
  }

  return (
    <>
    <EntityLinkProvider>
    <TooltipProvider>
      <div className="h-screen bg-slate-50/80 flex overflow-hidden">
        {/* 左侧导航栏 */}
        <div className="flex-shrink-0">
          <DispatchSidebar
            expandedMenus={expandedMenus}
            selectedStatus={selectedStatus}
            toggleMenu={toggleMenu}
            setSelectedStatus={setSelectedStatus}
          />
        </div>

        {/* 主内容区域 */}
        {isNestedRoute ? (
          // 如果是嵌套子路由，只渲染子路由内容（lazy-loaded via React.lazy in App.tsx）
          <div className="flex-1 h-full overflow-auto">
            <div className="h-full w-full">
              <ErrorBoundary>
                <Suspense fallback={
                  <div className="flex items-center justify-center h-full">
                    <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                }>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 p-5 flex flex-col min-h-0 overflow-hidden bg-slate-50/80">
            {/* 页面头部 */}
            <DispatchHeader
              onResetData={handleResetData}
              totalCount={containerData.length}
              isRealtimeConnected={isRealtimeConnected}
              lastUpdateTime={
                lastRealtimeUpdate && lastRefreshTime
                  ? (lastRealtimeUpdate > lastRefreshTime ? lastRealtimeUpdate : lastRefreshTime)
                  : lastRealtimeUpdate || lastRefreshTime || null
              }
              onDispatchTask={handleDispatchTask}
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onContainerMonitor={() => setShowMonitorModal(true)}
              onOperationFailures={() => setShowFailuresModal(true)}
              failureErrorCount={failureCounts.errors}
              failureWarningCount={failureCounts.warnings}
            />

            {/* 错误提示 */}
            {tasksError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between items-center">
                <span>Couldn't load tasks — {tasksError}</span>
                <button
                  onClick={() => refetchTasks()}
                  className="px-3 py-1 bg-red-100 hover:bg-red-200 rounded text-sm"
                >
                  Retry
                </button>
              </div>
            )}

            {/* 搜索过滤器区域 - 提供多维度的数据过滤功能 */}
            {tasksLoading && !containerData.length ? (
              <FiltersSkeleton />
            ) : (
              <SearchFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedDriver={selectedDriver}
                setSelectedDriver={setSelectedDriver}
                selectedShipper={selectedShipper}
                setSelectedShipper={setSelectedShipper}
                selectedOrderingParty={selectedOrderingParty}
                setSelectedOrderingParty={setSelectedOrderingParty}
                importExportFilter={importExportFilter}
                setImportExportFilter={setImportExportFilter}
                destinationFilter={destinationFilter}
                setDestinationFilter={setDestinationFilter}
                portFilter={portFilter}
                setPortFilter={setPortFilter}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                lfdFilter={lfdFilter}
                setLfdFilter={setLfdFilter}
                customLfdDate={customLfdDate}
                setCustomLfdDate={setCustomLfdDate}
                completedTimeFilter={completedTimeFilter}
                setCompletedTimeFilter={setCompletedTimeFilter}
                customCompletedDate={customCompletedDate}
                setCustomCompletedDate={setCustomCompletedDate}
                drivers={drivers}
                destinations={warehouseOptions}
                importExportOptions={importExportOptions}
                statusConfig={statusConfig}
                shippers={shippers}
                orderingParties={orderingParties}
                ports={portOptions}
              />
            )}

            {/* 只拉回来一部分任务时的提醒。齐全时这一行根本不存在。 */}
            {isTruncated && (
              <div
                role="status"
                className="mb-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Showing {containerData.length} of {totalTasks} tasks — the rest did not load.
                  Filters, counts and sorting only cover what you see here. Narrow the filters to get a complete list.
                </span>
              </div>
            )}

            {/* 主要内容：数据表格和统计面板 */}
            <ErrorBoundary>
              <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
                {/* 数据表格区域 - 显示容器任务列表，支持排序、选择、展开等功能 */}
                {tasksLoading && !containerData.length ? (
                  <div className="flex-1 overflow-auto">
                    <TableSkeleton rows={10} columns={8} />
                  </div>
                ) : (
                  <DispatchTable
                    sortedData={paginationResult.currentPageData}
                    allSortedData={sortedData}
                    examGroupingActive={selectedStatus === 'on-hold'}
                    paginationResult={paginationResult}
                    selectedTasks={selectedTasks}
                    expandedRows={expandedRows}
                    markedTaskIds={markedTaskIds}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    statusConfig={statusConfig}
                    taskTypeConfig={taskTypeConfig}
                    drivers={drivers}
                    handleSelectAll={handleSelectAll}
                    handleSelectTask={handleSelectTask}
                    toggleRowExpansion={toggleRowExpansion}
                    toggleMarked={toggleMarked}
                    handleSort={handleSort}
                    formatDateTime={formatDateTime}
                    getLFDUrgency={getLFDUrgency}
                    getLFDStyles={getLFDStyles}
                    handleDispatchTask={handleDispatchTask}
                    onEditContainerDetails={handleEditContainer}
                    onUpdateContainer={handleUpdateContainer}
                    onServerUpdate={refetchTasks}
                    autoOpenTaskId={autoOpenTaskId}
                    onAutoOpenHandled={() => setAutoOpenTaskId(null)}
                    onFixPlate={handleFixPlate}
                    onGateFee={handleGateFee}
                    onBatchBook={handleBatchBook}
                    selectedOrderingParty={selectedOrderingParty}
                    onOrderingPartyClick={handleOrderingPartyClick}
                  />
                )}

                {/* 右侧统计面板 - 显示各种状态统计和快速操作按钮 */}
                {tasksLoading && !containerData.length ? (
                  <div className="w-64 flex-shrink-0">
                    <StatisticsSkeleton />
                  </div>
                ) : (
                  <StatisticsCards
                    total={stats.total}
                    onHold={stats.onHold}
                    vessel={stats.vessel}
                    released={stats.released}
                    unassigned={stats.unassigned}
                    assigned={stats.assigned}
                    picking={stats.picking}
                    delivering={stats.delivering}
                    onStopYard={stats.onStopYard}
                    loading={stats.loading}
                    empty={stats.empty}
                    loaded={stats.loaded}
                    returning={stats.returning}
                    completed={stats.completed}
                    cancelled={stats.cancelled}
                    pause={stats.pause}
                    yardStored={stats.yardStored}
                    pccHold={stats.pccHold}
                    uncompleted={stats.uncompleted}
                    marked={stats.marked}
                    setSelectedStatus={setSelectedStatus}
                    setShowMarkedOnly={setShowMarkedOnly}
                    showMarkedOnly={showMarkedOnly}
                    selectedStatus={selectedStatus}
                    setCompletedTimeFilter={setCompletedTimeFilter}
                  />
                )}
              </div>
            </ErrorBoundary>
          </div>
        )}
      </div>
    </TooltipProvider>

    {/* 这三个弹窗必须留在 EntityLinkProvider 里面 —— 它们都要靠 useEntityLink
        打开任务详情，而 provider 外面拿到的是 no-op 兜底：点了毫无反应，
        连报错都没有。 */}

    {/* Container Monitor Modal */}
    <ContainerMonitorModal
      open={showMonitorModal}
      onClose={() => setShowMonitorModal(false)}
    />

    {/* Failed Operations centre */}
    <OperationFailuresModal
      open={showFailuresModal}
      onClose={() => setShowFailuresModal(false)}
    />

    {/* 批量预约 —— 一个实例挂在页面这一层，不进表格行 */}
    <GateFeeBatchModal
      open={gateFeeAnchorId !== null}
      onClose={() => setGateFeeAnchorId(null)}
      anchorTaskId={gateFeeAnchorId}
      onPay={handleGateFeeConfirm}
    />
    <BatchBookingModal
      open={batchBookAnchorId !== null}
      anchorTaskId={batchBookAnchorId}
      onClose={() => setBatchBookAnchorId(null)}
      onBook={handleBatchBookConfirm}
    />
    </EntityLinkProvider>
    </>
  )
}